import { Link } from "react-router-dom";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { PILLARS, IMAGES } from "../data/content";
import { usePageMeta } from "../hooks/use-page-meta";

const VALUES = ["Presence", "Precision", "Privacy", "Hospitality"];

export default function About() {
  usePageMeta(
    "About | Celebrity Bouncers — Premium Event Security, Vijayawada",
    "Celebrity Bouncers is a premium event security and VIP support team based in Vijayawada, serving Andhra Pradesh and Telangana."
  );
  return (
    <>
      <PageHero
        testid="about-page-hero"
        eyebrow="About"
        title="Premium Event Security, Practised as a Craft"
        description="Based in Vijayawada and serving Andhra Pradesh and Telangana, Celebrity Bouncers exists for occasions where atmosphere matters as much as order."
      />
      <section className="container-x py-20 sm:py-28" data-testid="about-story">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="eyebrow">Who We Are</p>
              <h2 className="mt-4 font-serif text-3xl font-medium leading-tight text-ivory sm:text-4xl">
                Security Professionals with the Manner of Hospitality
              </h2>
              <div className="mt-8 space-y-6 text-base leading-relaxed text-steel">
                <p>
                  Celebrity Bouncers was formed around a simple observation: at premium events, the
                  best security is the kind guests barely notice. Our teams are selected for
                  discipline and presentation, briefed for each venue, and trained to interact with
                  guests courteously — because a wedding, a launch or a concert should never feel
                  like a checkpoint.
                </p>
                <p>
                  From celebrity movements and corporate summits to family weddings and public
                  gatherings, we plan every deployment in advance: access points, movement areas,
                  sensitive zones and reporting lines. On the day, a dedicated coordinator keeps
                  organisers informed and guests comfortable.
                </p>
                <p>
                  We make no claims we cannot stand behind. What we offer is preparation, presence
                  and professionalism — delivered quietly.
                </p>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <div className="relative h-80 overflow-hidden sm:h-[460px]">
                <img
                  src={IMAGES.venue}
                  alt="An elegantly arranged premium event venue with warm lighting"
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-obsidian/70 to-transparent" />
                <p className="absolute bottom-4 left-4 text-[10px] uppercase tracking-[0.25em] text-ivory/60">
                  Representative imagery
                </p>
              </div>
              <div className="mt-6 grid grid-cols-2 gap-px bg-white/5 border border-white/5">
                {VALUES.map((v) => (
                  <div key={v} data-testid={`about-value-${v.toLowerCase()}`} className="bg-obsidian p-5 text-center">
                    <span className="font-serif text-lg italic text-gold">{v}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
      <section className="border-t border-gold/10 bg-navy/40 py-20 sm:py-28" data-testid="about-approach">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow">Our Approach</p>
            <h2 className="mt-4 max-w-2xl font-serif text-3xl font-medium text-ivory sm:text-4xl">
              Three Pillars, One Standard
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-px bg-white/5 sm:grid-cols-3">
            {PILLARS.map((p, i) => (
              <Reveal key={p.num} delay={i * 0.1} className="bg-obsidian">
                <div className="p-8 sm:p-10">
                  <span className="font-display text-3xl text-gold/40">{p.num}</span>
                  <h3 className="mt-5 font-serif text-xl font-medium uppercase tracking-[0.1em] text-ivory">{p.title}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-steel">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-12">
            <Link
              to="/contact"
              data-testid="about-enquire-btn"
              className="inline-block bg-gold px-8 py-4 text-[13px] font-bold uppercase tracking-[0.2em] text-obsidian transition-colors duration-300 hover:bg-gold-soft"
            >
              Meet the Team — Start with a Call
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
